import { useEffect, useRef, useState } from "react";

import { AvField, AvForm } from "availity-reactstrap-validation";
import { toast } from "react-toastify";

import DeleteButton from "../../components/DeleteButton";
import PlusIcon from "../../components/PlusButton";
import Details from "./Details";
import List from "./List";

import * as _service from "../../service/payments.service";

import { getDate, getTime, toTop } from "../../helpers/functions";
import Followup from "./Followup";
import SearchSelect from "../../components/SearchSelect";
import { Label } from "reactstrap";

const Payments = () => {
  const formRef = useRef();

  const date = getDate();
  const time = getTime();

  const initialState = { date, time, remarks: "", parts: [{ part: null, amount: "" }], total: 0 };

  const limit = 100;
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState();

  const [data, setData] = useState([]);

  const [search, setSearch] = useState("");
  const [selectedFields, setSelectedFields] = useState({});
  const [masterObject, setMasterObject] = useState({ ...initialState });

  const [modalData, setModalData] = useState({ show: false });

  useEffect(() => {
    handleTableData(1);
    setPage(1);
  }, [search]); // eslint-disable-line

  const handleTableData = async (currentPage = page) => {
    try {
      const response = await _service.list({ page: currentPage, limit, search });

      const { data, count } = response;
      setData(data);
      setTotalCount(count);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      const method = masterObject.id ? _service.update : _service.create;
      const response = await method(masterObject);

      toast.success(response.message);
      reset();
    } catch (error) {
      console.error(error);
      toast.error(error?.message);
    }
  };

  const handleUpdate = async (id) => {
    try {
      const response = await _service.detailsForUpdate(id);

      const { data = {} } = response;

      setMasterObject({ ...data, total: data.amount, id });
      toTop();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectChange = ({ selected, name }) => {
    setSelectedFields((prev) => ({ ...prev, [name]: selected }));
    handleValueChange({ name, value: selected?.value, selected });
  };

  const handleValueChange = ({ value, name, index, selected }) => {
    setMasterObject((prev) => {
      const newParts = [...prev.parts];
      if (index >= 0) {
        newParts[index] = { ...newParts[index], [name]: value };

        if (newParts[index].amount > 0 && newParts[index].quantity > 0) {
          newParts[index].total = newParts[index].amount * newParts[index].quantity;
        } else {
          newParts[index].total = 0;
        }
      } else prev[name] = value;

      if (name === "customer") {
        prev.name = selected?.name;
        prev.mobile = selected?.mobile;
      }

      const total = newParts.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);

      return {
        ...prev,
        parts: newParts,
        total: total + Number(prev.labourCharge || 0) - Number(prev.discount || 0),
      };
    });
  };

  const addMore = () => {
    setMasterObject((prev) => ({
      ...prev,
      parts: [...prev.parts, { part: null, amount: "" }],
    }));
  };

  const handleDelete = (index) => {
    if (masterObject.parts.length <= 1) {
      toast.warning("You need to have at least one part");
      return;
    }

    setMasterObject((prev) => {
      const newParts = [...prev.parts];
      newParts.splice(index, 1);

      const total = newParts.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);

      return {
        ...prev,
        parts: newParts,
        total: total + Number(prev.labourCharge || 0) - Number(prev.discount || 0),
      };
    });
  };

  const handleToggle = ({ show = false, view = false, add = false, id } = {}) => {
    setModalData({ show, view, add, id });
    if (!show) handleTableData();
  };

  const handlePageChange = (value) => {
    setPage(value);
    handleTableData(value);
  };

  const reset = () => {
    setPage(1);
    formRef.current?.reset();
    setMasterObject({ ...initialState });
    setSelectedFields({});
    handleTableData(1);
  };

  return (
    <>
      <h5 className="page-title">Payments</h5>
      <div className="card">
        <div className="card-body">
          {!masterObject.id && (
            <div className="row">
              <div className="col-md-4 mb-3">
                <Label>Customer</Label>
                <SearchSelect
                  api="customer/search"
                  value={selectedFields?.customer || ""}
                  defaultValue={selectedFields?.customer || ""}
                  onChange={(selected) => handleSelectChange({ selected, name: "customer" })}
                />
              </div>
            </div>
          )}
          <AvForm ref={formRef} onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-2">
                <Label>Date</Label>
                <AvField
                  type="date"
                  name="date"
                  value={masterObject?.date || ""}
                  defaultValue={masterObject?.date || ""}
                  validate={{ required: { value: true } }}
                  errorMessage="Enter Date"
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>
              <div className="col-md-2">
                <Label>Time</Label>
                <AvField
                  type="time"
                  name="time"
                  value={masterObject?.time || ""}
                  defaultValue={masterObject?.time || ""}
                  validate={{ required: { value: true } }}
                  errorMessage="Enter Time"
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>
              <div className="col-md-3">
                <Label>Customer</Label>
                <AvField
                  type="name"
                  name="name"
                  disabled={masterObject.customer}
                  placeholder="Customer Name"
                  value={masterObject?.name || ""}
                  validate={{ required: { value: true } }}
                  errorMessage="Enter Customer Name"
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>
              <div className="col-md-2">
                <Label>Mobile</Label>
                <AvField
                  type="number"
                  name="mobile"
                  disabled={masterObject.customer}
                  placeholder="Customer Mobile"
                  validate={{ required: { value: true } }}
                  errorMessage="Enter Mobile"
                  value={masterObject?.mobile || ""}
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>
              <div className="col-md-3">
                <Label>Boat Name</Label>
                <AvField
                  name="boat"
                  placeholder="Boat Name"
                  value={masterObject?.boat || ""}
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>
              <div className="col-md-2">
                <Label>Labour Charge</Label>
                <AvField
                  type="number"
                  name="labourCharge"
                  placeholder="Labour Charge"
                  value={masterObject?.labourCharge || ""}
                  validate={{ min: { value: 1, errorMessage: "Amount must be greater than zero" } }}
                  errorMessage="Enter Labour Charge"
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>
              <div className="col-md-3">
                <Label>Description</Label>
                <AvField
                  type="textarea"
                  rows={1}
                  name="remarks"
                  placeholder="Description"
                  value={masterObject?.remarks || ""}
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>

              <div className="col mb-3" style={{ maxWidth: "fit-content" }}>
                <div className="total-amount-display d-flex align-items-center gap-2">
                  <Label>Total Amount :</Label>
                  <div className="total-amount-value">
                    {masterObject.total?.toFixed(2) || "0.00"}
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <Label>Discount</Label>
                <AvField
                  type="number"
                  name="discount"
                  placeholder="Discount"
                  value={masterObject?.discount || ""}
                  validate={{
                    min: { value: 0, errorMessage: "Amount must be greater than or equal zero" },
                  }}
                  errorMessage="Enter Discount"
                  onChange={(e) => handleValueChange(e.target)}
                />
              </div>
              {!masterObject.id ? (
                <div className="col-md-2">
                  <Label>Paid Amount</Label>
                  <AvField
                    type="number"
                    name="paidAmount"
                    placeholder="Paid Amount"
                    value={masterObject?.paidAmount || ""}
                    validate={{
                      min: { value: 0, errorMessage: "Amount must be greater than or equal zero" },
                    }}
                    errorMessage="Enter Paid Amount"
                    onChange={(e) => handleValueChange(e.target)}
                  />
                </div>
              ) : (
                ""
              )}
            </div>

            {masterObject.parts?.map((row, index) => (
              <div className="row mb-3" key={index}>
                <div className="col-9 col-md-3">
                  <Label>Spare Part</Label>
                  <AvField
                    type="text"
                    name={`part-${index}`}
                    placeholder="Part"
                    value={row.part || ""}
                    onChange={(e) => handleValueChange({ ...e.target, name: "part", index })}
                  />
                </div>
                <div
                  className="col-3 col-md-2 gap-2 p-0 d-flex d-md-none"
                  style={{ marginTop: "35px" }}
                >
                  <DeleteButton
                    onClick={() => handleDelete(index)}
                    hidden={masterObject.parts?.length <= 1}
                  />
                  <PlusIcon onClick={addMore} hidden={index + 1 !== masterObject.parts?.length} />
                </div>
                <div className="col-4 col-md-2">
                  <Label>Amount</Label>
                  <AvField
                    type="number"
                    disabled={!row.part}
                    name={`amount-${index}`}
                    placeholder="Amount"
                    value={row.amount || ""}
                    onChange={(e) => handleValueChange({ ...e.target, name: "amount", index })}
                  />
                </div>
                <div className="col-4 col-md-2">
                  <Label>Quantity</Label>
                  <AvField
                    type="number"
                    disabled={!row.part}
                    name={`quantity-${index}`}
                    placeholder="Quantity"
                    value={row.quantity || ""}
                    onChange={(e) => handleValueChange({ ...e.target, name: "quantity", index })}
                  />
                </div>
                <div className="col-4 col-md-3" style={{ maxWidth: "fit-content" }}>
                  <div className="total-amount-display d-flex align-items-center gap-2">
                    <Label>Total :</Label>
                    <div className="total-amount-value">{row.total?.toFixed(2) || "0.00"}</div>
                  </div>
                </div>
                <div
                  className="col-2 col-md-2 gap-2 p-0 d-none d-md-flex"
                  style={{ marginTop: "35px" }}
                >
                  <DeleteButton
                    onClick={() => handleDelete(index)}
                    hidden={masterObject.parts?.length <= 1}
                  />
                  <PlusIcon onClick={addMore} hidden={index + 1 !== masterObject.parts?.length} />
                </div>
              </div>
            ))}

            <div className="row">
              <div className="col-md-3 mb-3 d-flex gap-2">
                <button className="btn btn-submit">{masterObject.id ? "Update" : "Submit"}</button>
                <button className="btn btn-reset" type="reset" onClick={reset}>
                  Reset
                </button>
              </div>
            </div>
          </AvForm>
        </div>
      </div>
      <List
        data={data}
        onEdit={handleUpdate}
        onView={handleToggle}
        onDelete={reset}
        page={page}
        limit={limit}
        count={totalCount}
        onPageChange={handlePageChange}
        searchValue={search}
        onSearch={(value) => setSearch(value)}
      />
      <Details {...modalData} isOpen={modalData.view} onToggle={() => handleToggle()} />
      <Followup {...modalData} isOpen={modalData.add} onToggle={() => handleToggle()} />
    </>
  );
};

export default Payments;
